import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Button, Spin } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { getProductExtensionCategories, changeExtensionCategoriesOrder } from "../../../service";
import { AdditionCategorySection } from "../../molecules/menu-addition-category-section/AdditionCategorySection";
import { AddExtensionCategoryModal } from "../../molecules/add-extension-category-modal/AddExtensionCategoryModal";
import { colors, spacing } from "../../../styles/theme";
import { useAdditionsTour } from "../../../contexts/additions-tour-context/useAdditionsTour";
import { useResponsive } from "../../../hooks/useResponsive";

const TabHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${spacing.xl};
`;

const TabDescription = styled.p`
  font-size: 14px;
  color: ${colors.textSecondary};
  margin: 0;
  max-width: 600px;
`;

const CategoriesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.lg};
`;

export const AdditionsTab: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [categories, setCategories] = useState<any[]>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newCategoryId, setNewCategoryId] = useState<number | null>(null);
  const categoryRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});
  const { addGroupBtnRef, isTourOpen, activeTour, tourStep, advanceStep } = useAdditionsTour();
  const { isMobile } = useResponsive();

  const { isLoading, data: queryData } = useQuery<any, Error>(
    ["/product-extension-categories"],
    () => getProductExtensionCategories(),
    {
      onSuccess(data) {
        setCategories(data.sort((a: any, b: any) => a.menuPosition - b.menuPosition));
      },
    }
  );

  // Synchronizuj lokalny stan z query cache (dla optimistic updates z modalu edycji)
  useEffect(() => {
    if (queryData && Array.isArray(queryData)) {
      // Zachowaj aktualną kolejność kategorii, ale zaktualizuj ich dane
      setCategories((currentCategories) => {
        if (currentCategories.length === 0) {
          return queryData.sort((a: any, b: any) => a.menuPosition - b.menuPosition);
        }
        // Mapuj aktualne kategorie z nowymi danymi, zachowując kolejność
        const currentIds = currentCategories.map((c) => c.id);
        const updatedCategories = currentCategories.map((cat) => {
          const updated = queryData.find((q: any) => q.id === cat.id);
          return updated ? { ...cat, ...updated } : cat;
        });
        // Dodaj nowe kategorie (jeśli są)
        const newCategories = queryData.filter((q: any) => !currentIds.includes(q.id));
        return [...updatedCategories, ...newCategories];
      });
    }
  }, [queryData]);

  const { mutateAsync: changeCategoryOrderMutation } = useMutation(
    changeExtensionCategoriesOrder,
    {
      onSuccess: (data) => {
        setCategories(data.sort((a: any, b: any) => a.menuPosition - b.menuPosition));
        queryClient.invalidateQueries(["/product-extension-categories"]);
      },
    }
  );

  const handleMoveCategoryUp = async (categoryId: number) => {
    const currentIndex = categories.findIndex((cat) => cat.id === categoryId);
    if (currentIndex <= 0) return;

    const newCategories = [...categories];
    [newCategories[currentIndex - 1], newCategories[currentIndex]] = [
      newCategories[currentIndex],
      newCategories[currentIndex - 1],
    ];

    const orderedIds = newCategories.map((cat) => cat.id);
    await changeCategoryOrderMutation({ categoriesIds: orderedIds });
  };

  const handleMoveCategoryDown = async (categoryId: number) => {
    const currentIndex = categories.findIndex((cat) => cat.id === categoryId);
    if (currentIndex < 0 || currentIndex >= categories.length - 1) return;

    const newCategories = [...categories];
    [newCategories[currentIndex], newCategories[currentIndex + 1]] = [
      newCategories[currentIndex + 1],
      newCategories[currentIndex],
    ];

    const orderedIds = newCategories.map((cat) => cat.id);
    await changeCategoryOrderMutation({ categoriesIds: orderedIds });
  };

  const handleAddCategory = () => {
    setIsAddModalOpen(true);
  };

  const handleCategoryCreated = (categoryId: number) => {
    setNewCategoryId(categoryId);
    // Tour: advance from HIDDEN_GROUP step to ADD_ADDITION step
    // Desktop: HIDDEN_GROUP=5, Mobile: HIDDEN_GROUP=6
    const hiddenGroupStep = isMobile ? 6 : 5;
    if (isTourOpen && activeTour === "create-additions" && tourStep === hiddenGroupStep) {
      setTimeout(() => advanceStep(hiddenGroupStep), 400);
    }
  };

  // Scroll do nowo utworzonej kategorii
  useEffect(() => {
    if (newCategoryId && categoryRefs.current[newCategoryId]) {
      setTimeout(() => {
        categoryRefs.current[newCategoryId]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        setNewCategoryId(null);
      }, 100);
    }
  }, [newCategoryId, categories]);

  if (isLoading) {
    return <Spin spinning={true} />;
  }

  return (
    <>
      <TabHeader>
        <TabDescription>
          {t("additionsTab.description")}
        </TabDescription>
        <Button
          ref={addGroupBtnRef as any}
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            handleAddCategory();
            // Tour: advance from ADD_GROUP step to HIDDEN_GROUP step
            // Desktop: ADD_GROUP=4, Mobile: ADD_GROUP=5
            const addGroupStep = isMobile ? 5 : 4;
            if (isTourOpen && activeTour === "create-additions" && tourStep === addGroupStep) {
              setTimeout(() => advanceStep(addGroupStep), 200);
            }
          }}
        >
          {t("additionsTab.addGroupButton")}
        </Button>
      </TabHeader>

      <CategoriesContainer>
        {categories.map((category, index) => (
          <div
            key={category.id}
            ref={(el) => (categoryRefs.current[category.id] = el)}
          >
            <AdditionCategorySection
              category={category}
              onMoveUp={() => handleMoveCategoryUp(category.id)}
              onMoveDown={() => handleMoveCategoryDown(category.id)}
              canMoveUp={index > 0}
              canMoveDown={index < categories.length - 1}
            />
          </div>
        ))}
      </CategoriesContainer>

      <AddExtensionCategoryModal
        open={isAddModalOpen}
        onCancel={() => setIsAddModalOpen(false)}
        onCreated={handleCategoryCreated}
      />
    </>
  );
};
